/**
 * `/parts-flow` — the parts pipeline. Port of the demo's `renderPartsflow()`
 * (line 2788) and `renderBom()` (line 2798).
 *
 * Parts sales, allocation and inventory as one flow across four tabs: the order
 * pipeline from intake to delivery, predicted demand against what is on the
 * shelf, stock control by location, and the digital parts books.
 *
 * The pipeline is the point. An order starts at an alert or a job rather than a
 * phone call: the agent resolves the part, checks the site store and the
 * distribution centre, prices it and picks road or air by the failure window.
 * Every requisition it drafts waits here for a person.
 *
 * ## Data
 *
 * One domain — `parts` — plus the lake catalogue on the BOM tab, where each
 * parts book's page thumbnail is fetched one document at a time.
 *
 * ## Declared omissions
 *
 * - **Approving for real.** "Approve" toasts, as the demo's button does. In
 *   production the approval is written to the parts portal and to Maximo or
 *   SAP PM.
 * - **Seventeen thumbnails at once.** The demo decodes every parts-book
 *   thumbnail on every render of the tab; the port narrows to the model family
 *   in view, which is the family the reader is reading the BOM for.
 *
 * @see src/versions/v1/modules/service/PartsFlowScene.transform.ts
 * @see docs/logic/workflows/parts-flow.md
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Package } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  InlineError,
  Input,
  SkeletonRows,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { MINES } from '../../data/core';
import type { LakeIndex } from '../../schema/document';
import { predictedUsage, type PredictedUsageRow } from '../../shared/oem-engine';
import { SceneSection, SourceLink, TonePill } from '../../shared/oem-tiles';
import { useToast } from '../../shared/toast/ToastContext';
import { loadLakeDoc, loadLakeIndex, useOemData, type ModelFamily } from '../../shared/useOemData';
import { useRowsDrawer, type RowsDrawer } from './source-slice';
import {
  belowMinimum,
  bomFamilies,
  bomRows,
  bookSections,
  isPartsFlowTab,
  locationStock,
  PARTS_FLOW_TABS,
  partsBooks,
  pipeline,
  type BomRow,
  type OrderCard,
  type PartsCtx,
  type PartsFlowTab,
  type PipelineColumnView,
} from './PartsFlowScene.transform';

/** Demo line 2791. The page's standfirst, verbatim. */
const STANDFIRST =
  'Parts sales, allocation and inventory as one flow: intake from the alert or the job, ' +
  'processing, transit and delivery on the left; predicted usage against the shelf and the ' +
  'digital parts book on the right. Every requisition the agent drafts waits here for a person.';

const TAB_ITEMS = PARTS_FLOW_TABS.map(([id, label]) => ({ id, label }));

export function PartsFlowScene() {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab: PartsFlowTab = isPartsFlowTab(requested) ? requested : 'pipeline';

  const parts = useOemData('parts');

  const ctx = useMemo<PartsCtx | null>(() => {
    if (!parts.data) return null;
    return {
      orders: parts.data.orders,
      parts: parts.data.parts,
      inventory: parts.data.inventory,
      catbaseBom: parts.data.catbase_bom,
      catbaseSections: parts.data.catbase_sections,
    };
  }, [parts.data]);

  const belowMinCount = useMemo(
    () => (ctx ? ctx.inventory.filter((i) => i.qty_on_hand < i.min_level).length : 0),
    [ctx],
  );
  const draftCount = useMemo(
    () => (ctx ? ctx.orders.filter((o) => /Draft/.test(o.status)).length : 0),
    [ctx],
  );

  return (
    <SurfacePageLayout width="full">
      <Commentable name="PartsFlowScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-6" as="div">
            <SurfacePageHeader
              kicker="Workflows"
              title="Parts pipeline"
              description={STANDFIRST}
              actions={
                <div className="flex items-center gap-2">
                  <span className="mono rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] text-muted dark:bg-zinc-800">
                    {draftCount} requisitions to approve · {belowMinCount} stock lines below minimum
                  </span>
                  <HelpAnchor doc="workflows/parts-flow" />
                </div>
              }
            />

            <AskStrip view="partsflow" />

            <Commentable name="PartsFlowTabs">
              <div className="min-w-0">
                <SurfacePageTabs
                  items={TAB_ITEMS}
                  value={tab}
                  onChange={(next) => setParams({ tab: next }, { replace: true })}
                  aria-label="Parts pipeline"
                />
              </div>
            </Commentable>

            {parts.error ? (
              <InlineError>{`Parts data did not load: ${parts.error.message}`}</InlineError>
            ) : !ctx ? (
              <SkeletonRows count={8} />
            ) : tab === 'pipeline' ? (
              <PipelineTab ctx={ctx} />
            ) : tab === 'predict' ? (
              <PredictTab ctx={ctx} />
            ) : tab === 'inventory' ? (
              <InventoryTab ctx={ctx} />
            ) : (
              <BomTab ctx={ctx} />
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

// ---------------------------------------------------------------------------
// Order pipeline
// ---------------------------------------------------------------------------

function PipelineTab({ ctx }: { ctx: PartsCtx }) {
  const columns = useMemo(() => pipeline(ctx), [ctx]);
  const openRows = useRowsDrawer();

  return (
    <Commentable name="OrderPipeline">
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-4">
          {columns.map((column) => (
            <PipelineLane key={column.key} column={column} ctx={ctx} openRows={openRows} />
          ))}
        </div>
        <p className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
          An accent edge marks an order the Parts sales agent drafted; a red edge marks urgent or
          air freight; a green edge marks delivered. Approving here updates the row on screen only.
          In production the approval is written to the parts portal and to Maximo or SAP PM.
        </p>
      </div>
    </Commentable>
  );
}

function PipelineLane({
  column,
  ctx,
  openRows,
}: {
  column: PipelineColumnView;
  ctx: PartsCtx;
  openRows: RowsDrawer;
}) {
  return (
    <section
      className="flex min-w-0 flex-col gap-2 rounded-2xl border p-3"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-1)' }}
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3
          className="text-[12.5px] font-semibold uppercase tracking-[0.06em]"
          style={{ color: 'var(--ink-tertiary)' }}
        >
          {column.label}
        </h3>
        <span className="mono text-[12.5px]" style={{ color: 'var(--ink-secondary)' }}>
          {column.total}
        </span>
      </header>

      {column.cards.map((card) => (
        <OrderTile key={card.order.order_no} card={card} />
      ))}

      {column.overflow ? (
        <p className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
          +{column.overflow} more ·{' '}
          <SourceLink onClick={() => openRows('orders', ctx.orders)}>all orders</SourceLink>
        </p>
      ) : null}

      {column.total === 0 ? (
        <p className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
          Nothing in this stage.
        </p>
      ) : null}
    </section>
  );
}

function OrderTile({ card }: { card: OrderCard }) {
  const { show } = useToast();
  const [approved, setApproved] = useState(false);
  const edge = card.urgent
    ? 'var(--crit)'
    : card.byAgent
      ? 'var(--accent)'
      : card.delivered
        ? 'var(--band-good)'
        : 'var(--hairline)';

  return (
    <article
      className="rounded-xl border border-l-[3px] p-2.5"
      style={{
        borderColor: 'var(--hairline)',
        borderLeftColor: edge,
        background: 'var(--surface-2)',
      }}
    >
      <b className="mono block text-[11.5px]" style={{ color: 'var(--ink-primary)' }}>
        {card.order.order_no} · {card.order.part_no}
      </b>
      {card.description ? (
        <p className="text-[11.5px]" style={{ color: 'var(--ink-secondary)' }}>
          {card.description}
        </p>
      ) : null}
      <p className="mt-0.5 text-[10.5px]" style={{ color: 'var(--ink-tertiary)' }}>
        {card.priceLine} ·{' '}
        <Link
          to={`/machines/${card.order.machine_id}`}
          className="mono underline decoration-dotted underline-offset-[3px] hover:decoration-solid"
          style={{ color: 'var(--accent)' }}
        >
          {card.order.machine_id}
        </Link>{' '}
        {card.siteShort} · {card.order.linked_wo}
      </p>
      <p className="mt-1 text-[11px]" style={{ color: 'var(--ink-secondary)' }}>
        {card.order.status}
      </p>
      {card.approvable ? (
        <button
          type="button"
          disabled={approved}
          onClick={() => {
            setApproved(true);
            show(`${card.order.order_no} sent to the parts portal`);
          }}
          className="mt-1.5 rounded-lg px-2.5 py-1 text-[11.5px] font-medium transition-opacity disabled:cursor-default disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
          style={{ background: 'var(--accent)', color: 'var(--accent-text)' }}
        >
          {approved ? 'Approved' : 'Approve'}
        </button>
      ) : null}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Predictive usage
// ---------------------------------------------------------------------------

function PredictTab({ ctx }: { ctx: PartsCtx }) {
  const rows = useMemo(() => predictedUsage(ctx), [ctx]);
  const openRows = useRowsDrawer();

  return (
    <Commentable name="PredictedUsage">
      <div className="min-w-0">
        <SceneSection
          title="Predicted usage"
          subtitle="next 30 days, against the shelf"
          actions={
            <SourceLink
              onClick={() =>
                openRows('inv', ctx.inventory, { title: 'Inventory snapshot · all locations' })
              }
            >
              inventory rows
            </SourceLink>
          }
        >
          <Table className="min-w-[1040px]">
              <THead>
                <TR>
                  <TH>Part</TH>
                  <TH>Description</TH>
                  <TH>Site</TH>
                  <TH>Driven by</TH>
                  <TH className="text-right">Predicted 30 d</TH>
                  <TH className="text-right">Site store</TH>
                  <TH className="text-right">Min</TH>
                  <TH className="text-right">DC</TH>
                  <TH>Agent proposal</TH>
                  <TH className="text-right">Proposed min</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((r) => (
                  <PredictedRow key={`${r.part_no}|${r.mine}`} row={r} />
                ))}
              </TBody>
            </Table>
          <p className="mt-2 text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
            Predicted usage is the flagged components mapped to the parts they will consume inside
            their failure window — a major or very poor component weighs 1, a poor one 0.5 — per
            site, against the parts-portal snapshot. Proposed minimums are drafts for the Parts and
            Logistics Coordinator.
          </p>
        </SceneSection>
      </div>
    </Commentable>
  );
}

function PredictedRow({ row }: { row: PredictedUsageRow }) {
  const short = row.machines.filter((m, i, all) => all.indexOf(m) === i);
  return (
    <TR>
      <TD className="mono whitespace-nowrap">{row.part_no}</TD>
      <TD className="text-[11.5px]">
        {row.desc}{' '}
        {row.crit ? (
          <TonePill tone={row.crit === 'Critical' ? 'bad' : 'flat'}>{row.crit}</TonePill>
        ) : null}
      </TD>
      <TD className="whitespace-nowrap">{MINES[row.mine].name.split(' ')[0]}</TD>
      <TD className="text-[11px]">
        {short.map((m, i) => (
          <span key={m}>
            {i > 0 ? ' ' : ''}
            <Link
              to={`/machines/${m}`}
              className="mono underline decoration-dotted underline-offset-[3px] hover:decoration-solid"
              style={{ color: 'var(--accent)' }}
            >
              {m}
            </Link>
          </span>
        ))}
      </TD>
      <TD className="mono text-right tabular-nums">{row.demand}</TD>
      <TD
        className="mono text-right tabular-nums"
        style={row.gap > 0 ? { color: 'var(--crit)', fontWeight: 600 } : undefined}
      >
        {row.store}
      </TD>
      <TD className="mono text-right tabular-nums">{row.min}</TD>
      <TD className="mono text-right tabular-nums">{row.dcq}</TD>
      <TD
        className="text-[11.5px]"
        style={row.gap > 0 ? undefined : { color: 'var(--ink-tertiary)' }}
      >
        {row.prop}
      </TD>
      <TD className="mono text-right tabular-nums">{row.newmin}</TD>
    </TR>
  );
}

// ---------------------------------------------------------------------------
// Inventory control
// ---------------------------------------------------------------------------

function InventoryTab({ ctx }: { ctx: PartsCtx }) {
  const locations = useMemo(() => locationStock(ctx), [ctx]);
  const rows = useMemo(() => belowMinimum(ctx), [ctx]);
  const openRows = useRowsDrawer();

  return (
    <Commentable name="InventoryControl">
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-3 @2xl:grid-cols-3 @5xl:grid-cols-4">
          {locations.map((l) => (
            <button
              key={l.location}
              type="button"
              onClick={() =>
                openRows('inv', l.belowMin, {
                  title: `${l.location} · below minimum`,
                  filter: `location=${l.location}, qty_on_hand < min_level`,
                  total: l.lines.length,
                })
              }
              className="relative rounded-xl border p-3 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
              style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
            >
              <span
                className="block text-[10px] font-semibold uppercase tracking-[0.08em]"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                {l.location}
              </span>
              <span className="mt-1 flex items-baseline gap-1.5">
                <span
                  className="mono text-[22px] font-semibold leading-none"
                  style={{ color: l.belowMin.length ? 'var(--crit)' : 'var(--band-good)' }}
                >
                  {l.belowMin.length}
                </span>
                <span className="text-[11px]" style={{ color: 'var(--ink-secondary)' }}>
                  of {l.lines.length} lines below minimum
                </span>
              </span>
              <span
                className="mono mt-1.5 block truncate text-[10.5px]"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                {l.examples}
                {l.more ? ' …' : ''}
              </span>
            </button>
          ))}
        </div>

        <SceneSection
          title="Inventory control"
          subtitle="below-minimum lines across all locations"
          actions={
            <span className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
              {rows.length} of {ctx.inventory.length} stock lines
            </span>
          }
        >
          <Table className="min-w-[820px]">
              <THead>
                <TR>
                  <TH>Location</TH>
                  <TH>Part</TH>
                  <TH>Description</TH>
                  <TH className="text-right">On hand</TH>
                  <TH className="text-right">Min</TH>
                  <TH>Criticality</TH>
                  <TH className="text-right">Lead time</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((r) => (
                  <TR key={r.key}>
                    <TD className="whitespace-nowrap">{r.location}</TD>
                    <TD className="mono whitespace-nowrap">{r.part_no}</TD>
                    <TD className="text-[11.5px]">{r.description}</TD>
                    <TD className="mono text-right tabular-nums" style={{ color: 'var(--crit)' }}>
                      {r.qty_on_hand}
                    </TD>
                    <TD className="mono text-right tabular-nums">{r.min_level}</TD>
                    <TD>
                      {r.criticality ? (
                        <TonePill tone={r.criticality === 'Critical' ? 'bad' : 'flat'}>
                          {r.criticality}
                        </TonePill>
                      ) : null}
                    </TD>
                    <TD className="mono text-right tabular-nums">
                      {r.leadTimeDays == null ? '—' : `${r.leadTimeDays} d`}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
        </SceneSection>
      </div>
    </Commentable>
  );
}

// ---------------------------------------------------------------------------
// BOM lookup
// ---------------------------------------------------------------------------

function BomTab({ ctx }: { ctx: PartsCtx }) {
  const families = useMemo(() => bomFamilies(ctx), [ctx]);
  const [family, setFamily] = useState<ModelFamily>(() => families[0]);
  const [query, setQuery] = useState('');
  const [lake, setLake] = useState<LakeIndex | null>(null);

  useEffect(() => {
    let live = true;
    loadLakeIndex().then(
      (index) => live && setLake(index),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);

  const rows = useMemo(() => bomRows(ctx, family, query), [ctx, family, query]);
  const sections = useMemo(() => bookSections(ctx, family), [ctx, family]);
  const books = useMemo(() => partsBooks(lake?.entries ?? [], family), [lake, family]);

  return (
    <Commentable name="BomLookup">
      <div className="grid grid-cols-1 gap-6 @4xl:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
        <div className="space-y-6">
          <SceneSection title="Digital parts books" subtitle="CatBase">
            <p className="mb-3 text-[12.5px]" style={{ color: 'var(--ink-secondary)' }}>
              Exploded views with item numbers and the bill of materials behind them. Ask the
              assistant for "item 3 on MS-120 section 07" and it resolves the part, the price, the
              stock and a delivery date from here.
            </p>
            {books.length === 0 ? (
              <p className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                {lake
                  ? 'No published book for this family — its parts are fleet-wide consumables.'
                  : 'Loading the document catalogue…'}
              </p>
            ) : (
              <ul className="space-y-3">
                {books.map((book) => (
                  <PartsBookRow key={book.entry.slug} slug={book.entry.slug} label={book.label} />
                ))}
              </ul>
            )}
          </SceneSection>

          {sections.length ? (
            <SceneSection title="Book sections" subtitle={`${sections.length} published`}>
              <ul className="space-y-1.5">
                {sections.map((s) => (
                  <li key={s.key} className="text-[11.5px]">
                    <span className="mono" style={{ color: 'var(--ink-tertiary)' }}>
                      {s.model} S{String(s.section).padStart(2, '0')}
                    </span>{' '}
                    <span style={{ color: 'var(--ink-primary)' }}>{s.title}</span>{' '}
                    <span style={{ color: 'var(--ink-tertiary)' }}>
                      · {s.lines.length} item{s.lines.length === 1 ? '' : 's'}
                    </span>
                  </li>
                ))}
              </ul>
            </SceneSection>
          ) : null}
        </div>

        <SceneSection
          title="BOM export"
          subtitle={`${ctx.catbaseBom.length} lines`}
          actions={
            <span className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
              {rows.length} shown
            </span>
          }
        >
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="bom-family">
              Model family
            </label>
            <select
              id="bom-family"
              value={family}
              onChange={(e) => setFamily(e.target.value as ModelFamily)}
              className="rounded-lg border px-2.5 py-1.5 text-[12.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
              style={{
                borderColor: 'var(--hairline)',
                background: 'var(--surface-2)',
                color: 'var(--ink-primary)',
              }}
            >
              {families.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            <label className="sr-only" htmlFor="bom-query">
              Filter the bill of materials
            </label>
            <Input
              id="bom-query"
              value={query}
              placeholder="Filter…"
              onChange={(e) => setQuery(e.target.value)}
              className="max-w-[220px]"
            />
          </div>

          {rows.length === 0 ? (
            <EmptyState
              icon={<Package className="mx-auto h-8 w-8 text-zinc-400" strokeWidth={1.6} />}
              title="No lines match"
              description="Clear the filter or pick another model family."
            />
          ) : (
            <Table className="min-w-[780px]">
                <THead>
                  <TR>
                    <TH>Section</TH>
                    <TH className="text-right">Item</TH>
                    <TH>Part</TH>
                    <TH>Description</TH>
                    <TH className="text-right">A$</TH>
                    <TH className="text-right">Lead</TH>
                    <TH>Criticality</TH>
                    <TH className="text-right">Stock</TH>
                  </TR>
                </THead>
                <TBody>
                  {rows.map((r) => (
                    <BomTableRow key={r.key} row={r} />
                  ))}
                </TBody>
              </Table>
          )}
        </SceneSection>
      </div>
    </Commentable>
  );
}

/**
 * One parts book. The page thumbnail is the lake document's own base64 WebP,
 * fetched when the row mounts — the catalogue index carries only `has_thumb`.
 */
function PartsBookRow({ slug, label }: { slug: string; label: string }) {
  const [thumb, setThumb] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    loadLakeDoc(slug).then(
      (doc) => live && setThumb(doc.thumb ?? null),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [slug]);

  return (
    <li className="flex gap-3">
      <span
        className="block h-[92px] w-[68px] flex-shrink-0 overflow-hidden rounded border"
        style={{ borderColor: 'var(--hairline)', background: 'var(--surface-3)' }}
      >
        {thumb ? (
          <img
            src={`data:image/webp;base64,${thumb}`}
            alt=""
            className="h-full w-full object-cover object-top"
          />
        ) : null}
      </span>
      <div className="min-w-0">
        <b className="block text-[12.5px]" style={{ color: 'var(--ink-primary)' }}>
          {label}
        </b>
        <p className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
          PDF · page 1 shown · exploded view and BOM
        </p>
      </div>
    </li>
  );
}

function BomTableRow({ row }: { row: BomRow }) {
  return (
    <TR>
      <TD className="whitespace-nowrap">{row.section}</TD>
      <TD className="mono text-right tabular-nums">{row.item_no}</TD>
      <TD className="mono whitespace-nowrap">{row.part_no}</TD>
      <TD className="text-[11.5px]">{row.description}</TD>
      <TD className="mono text-right tabular-nums">
        {row.list_price_aud.toLocaleString('en-AU')}
      </TD>
      <TD className="mono text-right tabular-nums">{row.lead_time_days} d</TD>
      <TD>
        <TonePill tone={row.criticality === 'Critical' ? 'bad' : 'flat'}>
          {row.criticality}
        </TonePill>
      </TD>
      <TD className="mono text-right tabular-nums">{row.stock}</TD>
    </TR>
  );
}
